import { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter, faSpinner, faCircleInfo } from '@/fontawesome';
import { APIRoutes } from '@/services/api.routes';
import PageBasedPagination from '@/components/ui/PageBasedPagination';
import Tooltip from '@/components/ui/Tooltip';
import { showErrorToast } from '@/utils/toasts';
import {
  formatCurrencyCentValue,
  formatUSDCentValue,
  formatRFCDate,
  formatPercentage,
  formatDate,
  toCurrencyCentValue,
} from '@/utils/helpers';
import type { OptionContract, OptionRight } from '@/models/option-contract';
import type { SymbolShort } from '@/models/symbol';
import type { SortDirection } from '@/models/enums';
import type { ListOptionContractsOptions, OptionContractSortField } from '@/modelsAPI/option-contract';

const PAGE_SIZE = 50;

const SORT_OPTIONS: { value: OptionContractSortField; label: string }[] = [
  { value: 'updated', label: 'Aktualisiert' },
  { value: 'expiration', label: 'Verfall' },
  { value: 'daysToExpiration', label: 'Restlaufzeit (DTE)' },
  { value: 'strike', label: 'Strike' },
  { value: 'mid', label: 'Mid-Preis' },
  { value: 'yield', label: 'Rendite' },
  { value: 'impliedVolatility', label: 'Impl. Volatilität' },
  { value: 'delta', label: 'Delta' },
  { value: 'volume', label: 'Volumen' },
  { value: 'openInterest', label: 'Open Interest' },
  { value: 'underlying', label: 'Basiswert' },
];

// Table columns in render order. `tooltip` explains the value/field/unit (multiline, shown on header hover).
const COLUMNS: { label: string; tooltip: string }[] = [
  { label: 'Basiswert', tooltip: 'Basiswert\nDas zugrunde liegende Wertpapier (Ticker) der Option.' },
  { label: 'Typ', tooltip: 'Optionstyp\nCall = Recht zu kaufen\nPut = Recht zu verkaufen' },
  { label: 'Strike', tooltip: 'Ausübungspreis (Strike)\nPreis, zu dem der Basiswert gekauft (Call) bzw. verkauft (Put) werden kann.\nEinheit: Währung des Basiswerts.' },
  { label: 'Verfall', tooltip: 'Verfallsdatum\nLetzter Handelstag der Option.' },
  { label: 'DTE', tooltip: 'Days to Expiration\nRestlaufzeit in Kalendertagen bis zum Verfall.' },
  { label: 'Bid', tooltip: 'Geldkurs (Bid)\nHöchster Preis, den ein Käufer aktuell zahlt.\nEinheit: $ je Aktie (× Multiplikator 100 = je Kontrakt).' },
  { label: 'Ask', tooltip: 'Briefkurs (Ask)\nNiedrigster Preis, den ein Verkäufer aktuell akzeptiert.\nEinheit: $ je Aktie.' },
  { label: 'Mid', tooltip: 'Mittelkurs (Mid)\n(Bid + Ask) / 2 – fairer Richtpreis.\nEinheit: $ je Aktie.' },
  { label: 'Rendite', tooltip: 'Rendite\nPrämie (Mid) im Verhältnis zum Strike: Mid / Strike.\nEinheit: Prozent.' },
  { label: 'Spread%', tooltip: 'Geld-Brief-Spanne\n(Ask − Bid) / Mid, in Prozent.\nNiedriger = liquider / geringere Handelskosten.' },
  { label: 'IV', tooltip: 'Implizite Volatilität\nVom Markt erwartete Schwankungsbreite des Basiswerts p.a.\nEinheit: Prozent.' },
  { label: 'Delta', tooltip: 'Delta (Δ)\nPreisänderung der Option je 1 $ Kursänderung des Basiswerts.\nCall: 0 bis 1, Put: −1 bis 0.' },
  { label: 'Gamma', tooltip: 'Gamma (Γ)\nÄnderung des Delta je 1 $ Kursänderung des Basiswerts.' },
  { label: 'Theta', tooltip: 'Theta (Θ)\nTäglicher Zeitwertverlust der Option.\nEinheit: $ je Aktie und Tag (meist negativ).' },
  { label: 'Vol.', tooltip: 'Volumen\nAnzahl in der aktuellen Sitzung gehandelter Kontrakte.' },
  { label: 'OI', tooltip: 'Open Interest\nAnzahl aktuell offener (noch nicht geschlossener) Kontrakte.' },
  { label: 'Kurs Basiswert', tooltip: 'Kurs des Basiswerts\nAktueller Preis des zugrunde liegenden Wertpapiers.\nEinheit: $.' },
  { label: 'Aktualisiert', tooltip: 'Zuletzt aktualisiert\nZeitpunkt der letzten Datenaktualisierung dieser Option.' },
];

interface FilterState {
  symbolTicker: string;
  search: string;
  right: '' | OptionRight;
  dteMin: string;
  dteMax: string;
  strikeMin: string;
  strikeMax: string;
  ivMin: string;
  ivMax: string;
  deltaMin: string;
  deltaMax: string;
  yieldMin: string;
  yieldMax: string;
  sort: OptionContractSortField;
  sortDirection: SortDirection;
}

const emptyFilters: FilterState = {
  symbolTicker: '',
  search: '',
  right: '',
  dteMin: '',
  dteMax: '',
  strikeMin: '',
  strikeMax: '',
  ivMin: '',
  ivMax: '',
  deltaMin: '',
  deltaMax: '',
  yieldMin: '',
  yieldMax: '',
  sort: 'updated',
  sortDirection: 'DESC',
};

const parseFloatOpt = (s: string): number | undefined => {
  if (s.trim() === '') return undefined;
  const v = parseFloat(s);
  return isNaN(v) ? undefined : v;
};

const parseIntOpt = (s: string): number | undefined => {
  if (s.trim() === '') return undefined;
  const v = parseInt(s, 10);
  return isNaN(v) ? undefined : v;
};

export default function OptionScreenerPage() {
  const [filters, setFilters] = useState<FilterState>(emptyFilters);
  const [options, setOptions] = useState<OptionContract[]>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [underlyings, setUnderlyings] = useState<SymbolShort[]>([]);

  // load underlying list once for the dropdown
  useEffect(() => {
    APIRoutes.listOptionUnderlyings()
      .then(resp => setUnderlyings(resp.underlyings))
      .catch(() => showErrorToast('Basiswerte konnten nicht geladen werden'));
  }, []);

  const buildOptions = useCallback((): ListOptionContractsOptions => {
    const strikeMinDollar = parseFloatOpt(filters.strikeMin);
    const strikeMaxDollar = parseFloatOpt(filters.strikeMax);
    const ivMinPct = parseFloatOpt(filters.ivMin);
    const ivMaxPct = parseFloatOpt(filters.ivMax);
    const yieldMinPct = parseFloatOpt(filters.yieldMin);
    const yieldMaxPct = parseFloatOpt(filters.yieldMax);

    return {
      limit: PAGE_SIZE,
      next: currentPage > 1 ? `$${currentPage}` : undefined,
      symbolTicker: filters.symbolTicker !== '' ? filters.symbolTicker : undefined,
      search: filters.search.trim() !== '' ? filters.search.trim() : undefined,
      right: filters.right !== '' ? filters.right : undefined,
      dteMin: parseIntOpt(filters.dteMin),
      dteMax: parseIntOpt(filters.dteMax),
      strikeMin: strikeMinDollar !== undefined ? toCurrencyCentValue(strikeMinDollar) : undefined,
      strikeMax: strikeMaxDollar !== undefined ? toCurrencyCentValue(strikeMaxDollar) : undefined,
      ivMin: ivMinPct !== undefined ? ivMinPct / 100 : undefined,
      ivMax: ivMaxPct !== undefined ? ivMaxPct / 100 : undefined,
      deltaMin: parseFloatOpt(filters.deltaMin),
      deltaMax: parseFloatOpt(filters.deltaMax),
      yieldMin: yieldMinPct !== undefined ? yieldMinPct / 100 : undefined,
      yieldMax: yieldMaxPct !== undefined ? yieldMaxPct / 100 : undefined,
      sort: filters.sort,
      sortDirection: filters.sortDirection,
    };
  }, [filters, currentPage]);

  const fetchData = useCallback(() => {
    setLoading(true);
    APIRoutes.listOptionContracts(buildOptions())
      .then(resp => {
        setOptions(resp.options);
        setTotal(resp.total);
      })
      .catch(() => showErrorToast('Optionen konnten nicht geladen werden'))
      .finally(() => setLoading(false));
  }, [buildOptions]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // any filter change resets to page 1
  const updateFilter = <K extends keyof FilterState>(key: K, value: FilterState[K]) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const updateSort = (sort: OptionContractSortField, sortDirection: SortDirection) => {
    setFilters(prev => ({ ...prev, sort, sortDirection }));
    setCurrentPage(1);
  };

  const resetFilters = () => {
    setFilters(emptyFilters);
    setCurrentPage(1);
  };

  const inputClass =
    'w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500';
  const labelClass = 'block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1';
  const thClass = 'px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap';
  const tdClass = 'px-3 py-2 text-sm text-gray-900 dark:text-gray-100 whitespace-nowrap';

  return (
    <div className="p-4 md:p-6 max-w-full">
      <div className="flex items-center mb-6">
        <FontAwesomeIcon icon={faFilter} className="w-6 h-6 text-blue-600 dark:text-blue-400 mr-3" />
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Option Screener</h1>
      </div>

      {/* Filter bar */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          <div>
            <label className={labelClass}>Basiswert</label>
            <select className={inputClass} value={filters.symbolTicker} onChange={e => updateFilter('symbolTicker', e.target.value)}>
              <option value="">Alle</option>
              {underlyings.map(u => (
                <option key={u.id} value={u.ticker}>{u.ticker}</option>
              ))}
            </select>
          </div>

          <div>
            <label className={labelClass}>Suche</label>
            <input className={inputClass} type="text" placeholder="Ticker / Name" value={filters.search} onChange={e => updateFilter('search', e.target.value)} />
          </div>

          <div>
            <label className={labelClass}>Typ</label>
            <select className={inputClass} value={filters.right} onChange={e => updateFilter('right', e.target.value as '' | OptionRight)}>
              <option value="">Alle</option>
              <option value="CALL">Call</option>
              <option value="PUT">Put</option>
            </select>
          </div>

          <div>
            <label className={labelClass}>Restlaufzeit (Tage)</label>
            <div className="flex gap-2">
              <input className={inputClass} type="number" placeholder="min" value={filters.dteMin} onChange={e => updateFilter('dteMin', e.target.value)} />
              <input className={inputClass} type="number" placeholder="max" value={filters.dteMax} onChange={e => updateFilter('dteMax', e.target.value)} />
            </div>
          </div>

          <div>
            <label className={labelClass}>Strike ($)</label>
            <div className="flex gap-2">
              <input className={inputClass} type="number" placeholder="min" value={filters.strikeMin} onChange={e => updateFilter('strikeMin', e.target.value)} />
              <input className={inputClass} type="number" placeholder="max" value={filters.strikeMax} onChange={e => updateFilter('strikeMax', e.target.value)} />
            </div>
          </div>

          <div>
            <label className={labelClass}>Impl. Vol. (%)</label>
            <div className="flex gap-2">
              <input className={inputClass} type="number" placeholder="min" value={filters.ivMin} onChange={e => updateFilter('ivMin', e.target.value)} />
              <input className={inputClass} type="number" placeholder="max" value={filters.ivMax} onChange={e => updateFilter('ivMax', e.target.value)} />
            </div>
          </div>

          <div>
            <label className={labelClass}>Delta (-1..1)</label>
            <div className="flex gap-2">
              <input className={inputClass} type="number" step="0.1" placeholder="min" value={filters.deltaMin} onChange={e => updateFilter('deltaMin', e.target.value)} />
              <input className={inputClass} type="number" step="0.1" placeholder="max" value={filters.deltaMax} onChange={e => updateFilter('deltaMax', e.target.value)} />
            </div>
          </div>

          <div>
            <label className={labelClass}>Rendite (%)</label>
            <div className="flex gap-2">
              <input className={inputClass} type="number" step="0.1" placeholder="min" value={filters.yieldMin} onChange={e => updateFilter('yieldMin', e.target.value)} />
              <input className={inputClass} type="number" step="0.1" placeholder="max" value={filters.yieldMax} onChange={e => updateFilter('yieldMax', e.target.value)} />
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end mt-4">
          <button type="button" onClick={resetFilters} className="px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 cursor-pointer">
            Filter zurücksetzen
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <span className="text-sm text-gray-600 dark:text-gray-400">{total} Optionen gefunden</span>
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 whitespace-nowrap">Sortieren nach</label>
            <select
              className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
              value={`${filters.sort}:${filters.sortDirection}`}
              onChange={e => {
                const [field, dir] = e.target.value.split(':');
                updateSort(field as OptionContractSortField, dir as SortDirection);
              }}
            >
              {SORT_OPTIONS.flatMap(o => [
                <option key={`${o.value}:DESC`} value={`${o.value}:DESC`}>{o.label} ↓</option>,
                <option key={`${o.value}:ASC`} value={`${o.value}:ASC`}>{o.label} ↑</option>,
              ])}
            </select>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-gray-500 dark:text-gray-400">
            <FontAwesomeIcon icon={faSpinner} className="w-5 h-5 mr-2 animate-spin" /> Lade Optionen…
          </div>
        ) : options.length === 0 ? (
          <div className="text-center py-16 text-gray-500 dark:text-gray-400">Keine Optionen für die gewählten Filter gefunden.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900/50">
                <tr>
                  {COLUMNS.map(col => (
                    <th key={col.label} className={thClass}>
                      <Tooltip text={col.tooltip} position="bottom">
                        <span className="inline-flex cursor-help items-center gap-1">
                          {col.label}
                          <FontAwesomeIcon icon={faCircleInfo} className="w-3 h-3 text-gray-400 opacity-60" />
                        </span>
                      </Tooltip>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {options.map(o => (
                  <tr key={o.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className={tdClass + ' font-medium'}>{o.symbol !== null ? o.symbol.ticker : (o.symbolTicker ?? '—')}</td>
                    <td className={tdClass}>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${o.right === 'CALL' ? 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-200' : 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-200'}`}>
                        {o.right === 'CALL' ? 'Call' : 'Put'}
                      </span>
                    </td>
                    <td className={tdClass}>{formatCurrencyCentValue(o.strike, o.currency)}</td>
                    <td className={tdClass}>{formatRFCDate(o.expiration)}</td>
                    <td className={tdClass}>{o.daysToExpiration}</td>
                    <td className={tdClass}>{formatUSDCentValue(o.bid)}</td>
                    <td className={tdClass}>{formatUSDCentValue(o.ask)}</td>
                    <td className={tdClass}>{formatUSDCentValue(o.mid)}</td>
                    <td className={tdClass}>{formatPercentage(o.yield)}</td>
                    <td className={tdClass}>{formatPercentage(o.spreadPct)}</td>
                    <td className={tdClass}>{formatPercentage(o.impliedVolatility)}</td>
                    <td className={tdClass}>{o.delta.toFixed(3)}</td>
                    <td className={tdClass}>{o.gamma.toFixed(4)}</td>
                    <td className={tdClass}>{o.theta.toFixed(3)}</td>
                    <td className={tdClass}>{o.volume.toLocaleString('de-DE')}</td>
                    <td className={tdClass}>{o.openInterest.toLocaleString('de-DE')}</td>
                    <td className={tdClass}>{formatCurrencyCentValue(o.underlyingPrice, o.currency)}</td>
                    <td className={tdClass}>{formatDate(o.updated ?? o.created)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <PageBasedPagination
          currentPage={currentPage}
          totalCount={total}
          pageSize={PAGE_SIZE}
          onPageChange={setCurrentPage}
          loading={loading}
        />
      </div>
    </div>
  );
}
